import { signInUser } from 'Actions/AuthActions';
import { useMutationQuery } from 'Api/hooks/useMutationQuery';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import IntlMessages from 'Util/IntlMessages';
import TextInput from '../../common/TextInput';
import { onKeyPressSubmit } from 'Util/DataUtils';
import BasicButton from '../../common/BasicButton';
import CheckBox from '../../common/CheckBox';
import { loginError, BLANK_PHONE, BLANK_STAFF_ID, BLANK_PASSWORD } from 'Api/Errors';
import FormatNumberInput from '../../common/FormatNumberInput';
import NumberInput from '../../common/NumberInput';
import { useIntl } from 'react-intl';
import { Input } from 'reactstrap';
import { setCountry, setLanguage } from 'Actions/AppSettingsActions';
import moment from 'moment/min/moment-with-locales';
import { KAKAO_CHANNEL, ZALO_CHANNEL } from 'Constants/CustomerChannelLink';

const TYPE_ADMIN = 'TYPE_ADMIN';
const TYPE_STAFF = 'TYPE_STAFF';

const LoginSection = ({ onChangeJoin, onChangeFindPassword }) => {
  const { formatMessage } = useIntl();
  const { locale } = useSelector(state => state.settings.locale);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [type, setType] = useState(TYPE_ADMIN);
  const [phoneCountry, setPhoneCountry] = useState('82');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [staffId, setStaffId] = useState('');
  const [isChecked, setIsChecked] = useState(true);
  const [error, setError] = useState(null);
  const [clickCount, setClickCount] = useState(0);
  const [showCountrySelect, setShowCountrySelect] = useState(false);
  const [lastClickTime, setLastClickTime] = useState(0);
  const { country } = useSelector(state => state.settings);

  const { mutate } = useMutationQuery({
    method: 'post',
    url: '/auth/login',
    options: {
      onSuccess: ({ data }) => {
        if (isChecked) {
          localStorage.setItem('id', phone);
        } else {
          localStorage.removeItem('id');
        }
        dispatch(signInUser(data, navigate));
      },
      onError: error => {
        const message = error?.response?.data?.message;
        setError(message);
      },
    },
  });

  useEffect(() => {
    setPhone(localStorage.getItem('id') || '');
  }, []);

  useEffect(() => {
    if (locale === 'ko') {
      setPhoneCountry('82');
    } else {
      setPhoneCountry('84');
    }
  }, [locale]);

  const handlePhoneChange = values => {
    setPhone(values.value);
    setError(null);
  };

  const handleLogin = () => {
    if (phone === '') {
      setError(BLANK_PHONE);
      return;
    }
    if (type === TYPE_STAFF && staffId === '') {
      setError(BLANK_STAFF_ID);
      return;
    }
    if (password === '') {
      setError(BLANK_PASSWORD);
      return;
    }

    mutate({ phone, phoneCountry, password, ...(type === TYPE_STAFF && { staffId }) });
  };

  const isError = myError => {
    if (!error) return false;
    if (error === myError) return true;
    if ([BLANK_PHONE, BLANK_STAFF_ID, BLANK_PASSWORD].includes(error)) return false;
    return true;
  };

  const handleTitleClick = () => {
    const currentTime = Date.now();
    const timeDiff = currentTime - lastClickTime;

    if (timeDiff > 5000) {
      setClickCount(1);
    } else {
      setClickCount(prev => prev + 1);
    }

    setLastClickTime(currentTime);

    if (clickCount + 1 === 5) {
      setShowCountrySelect(true);
      setClickCount(0);
    }
  };

  const setCountryLanguage = number => {
    if (number === '82') {
      dispatch(setCountry('ko'));
      dispatch(
        setLanguage({
          languageId: 'korean',
          locale: 'ko',
          name: 'Korean',
          icon: 'ko',
        })
      );
      moment.locale('ko');
    } else {
      dispatch(setCountry('vi'));
      dispatch(
        setLanguage({
          languageId: 'vietnamese',
          locale: 'vi',
          name: 'Vietnamese',
          icon: 'vi',
        })
      );
      moment.locale('vi');
    }
  };

  const handleConsult = () => {
    if (country === 'vi') {
      window.open(ZALO_CHANNEL, '_blank');
    } else {
      window.open(KAKAO_CHANNEL, '_blank');
    }
  };

  return (
    <div className="inner-container">
      <div className="title-container">
        <div className="title-main no-drag" onClick={handleTitleClick}>
          <IntlMessages id="sign.title" />
        </div>
        <div className="title-sub">
          <IntlMessages id="signIn.subtitle" />
        </div>
      </div>
      <div className="form-container">
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {showCountrySelect && (
            <Input
              type="select"
              value={phoneCountry}
              onChange={e => {
                setPhoneCountry(e.target.value);
                setCountryLanguage(e.target.value);
              }}
              style={{ height: '50px', borderRadius: '10px', border: '1px solid #d9d9d9' }}
            >
              <option value="82">{formatMessage({ id: 'country.korea' })}</option>
              <option value="84">{formatMessage({ id: 'country.vietnam' })}</option>
            </Input>
          )}
          <FormatNumberInput
            isError={isError(BLANK_PHONE)}
            errorText={error === BLANK_PHONE && formatMessage({ id: loginError[error] })}
            label={country === 'vi' ? 'common.id' : 'seller.businessNumber'}
            placeholder={country === 'vi' ? 'signIn.vi.phone.placeholder' : 'sign.phone.message'}
            value={phone}
            onValueChange={handlePhoneChange}
            autoFocus
            isShowPlaceholder={country === 'vi'}
            focusLabel={country === 'vi' ? 'signIn.vi.phone.focus' : ''}
            format={country === 'vi' ? '####################' : '###-##-###############'}
          />
        </div>
        {type === TYPE_STAFF && (
          <NumberInput
            isError={isError(BLANK_STAFF_ID)}
            errorText={error === BLANK_STAFF_ID && formatMessage({ id: loginError[error] })}
            label="staff.no"
            value={staffId}
            onChange={e => {
              setStaffId(e.target.value);
              setError(null);
            }}
          />
        )}
        <TextInput
          isError={isError(BLANK_PASSWORD)}
          errorText={error === BLANK_PASSWORD ? formatMessage({ id: loginError[error] }) : error}
          label="common.password"
          placeholder="sign.password.message"
          value={password}
          onChange={e => {
            setPassword(e.target.value);
            setError(null);
          }}
          isPassword
          onKeyPress={e => {
            onKeyPressSubmit(e, handleLogin);
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <CheckBox label="signIn.login.idSave" isChecked={isChecked} onSetIsChecked={() => setIsChecked(x => !x)} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div className="find-password-wrapper cursor-pointer no-drag" onClick={onChangeFindPassword}>
              <IntlMessages id="signIn.login.findPassword" />
            </div>
            <div style={{ width: '1px', height: '8px', backgroundColor: '#dddddd' }} />
            <div className="find-password-wrapper cursor-pointer no-drag" onClick={handleConsult}>
              <IntlMessages id="signIn.join.consult" />
            </div>
          </div>
        </div>
        <BasicButton label="sign.title" onClick={handleLogin} style={{ backgroundColor: '#0567F9' }} disabled={phone === '' || password === ''} />
        <div className="join-text-wrapper" onClick={onChangeJoin}>
          <IntlMessages id="signIn.join.1" />
          <span className="join-text-blue">
            <IntlMessages id="signIn.join.2" />
          </span>
        </div>
      </div>
      <div className="extra-container">
        <div className="extra-divider-wrapper">
          <div className="extra-divider" />
          <div className="extra-divider-text">
            <IntlMessages id="signIn.extra.1" />
          </div>
          <div className="extra-divider" />
        </div>
        <div
          className="login-other"
          onClick={() => {
            setType(prevType => (prevType === TYPE_ADMIN ? TYPE_STAFF : TYPE_ADMIN));
            setStaffId('');
            setError(null);
          }}
        >
          <IntlMessages id={`signIn.extra.${type}`} />
        </div>
      </div>
    </div>
  );
};

export default LoginSection;
